import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import api from '../../../config/axiosInstance';
import TableList from './TableList';
import SearchBar from './SearchBar';
import ActionButtons from './ActionButtons';
import { Container, Typography, Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { StationContext } from '../../context/StationContext';

function PagePersonalList() {
  const theme = useTheme();
  const [listPersonal, setListPersonal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  const [title, setTitle] = useState('');
  const [showOnlyActive, setShowOnlyActive] = useState(true);
  const [buttonText, setButtonText] = useState({});
  const [filterTexts, setFilterTexts] = useState({
    buttonReloadText: 'Recargar',
    fieldSearchText: 'Buscar',
    fieldSwitchText: 'Mostrar solo activos',
  });
  const hasLoadedRef = useRef(false);

  const { selectedStation } = useContext(StationContext);

  const fetchData = useCallback(async () => {
    if (!selectedStation?.id) {
      setLoading(false);
      setListPersonal(null);
      setFilteredData([]);
      setTitle('');
      return;
    }

    setLoading(true);
    try {
      const response = await api.get(`personal?station=${encodeURIComponent(selectedStation.id)}`);

      if (response.data && response.data[0] && response.data[0].list_personal) {
        const data = response.data[0].list_personal;
        hasLoadedRef.current = true;
        setListPersonal(data);
        setFilteredData(data.data);
        setTitle(response.data[0].title);

        setButtonText({
          buttonAddPersonal: response.data[0].button_add_personal.text,
          buttonImportPersonal: response.data[0].button_import_personal.text,
        });

        setFilterTexts({
          buttonReloadText: response.data[0].filters.button_reload_text || 'Recargar',
          fieldSearchText: response.data[0].filters.field_search_text || 'Buscar',
          fieldSwitchText: response.data[0].filters.field_switch_text || 'Mostrar solo activos',
        });
      } else {
        console.error('La respuesta de la API no tiene el formato esperado.', response.data);
        if (!hasLoadedRef.current) {
          setListPersonal({ data: [] }); // Para que filteredData.length === 0 funcione
          setFilteredData([]);
          setTitle('');
        }
      }
    } catch (error) {
      console.error('Hubo un error al obtener los datos!', error);
      if (!hasLoadedRef.current) {
        setListPersonal({ data: [] });
        setFilteredData([]);
        setTitle('');
      }
    } finally {
      setLoading(false);
    }
  }, [selectedStation?.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (listPersonal) {
      let filtered = listPersonal.data || [];

      if (showOnlyActive) {
        filtered = filtered.filter((item) => item.enabled === 'ACTIVO');
      }

      filtered = filtered.filter((item) => {
        const fullName = `${item.name} ${item.surname}`.toLowerCase();
        const terms = searchTerm.toLowerCase().split(' ');
        return terms.every(term => fullName.includes(term)) ||
          Object.values(item).some(value => String(value).toLowerCase().includes(searchTerm.toLowerCase()));
      });

      setFilteredData(filtered);
    }
  }, [searchTerm, listPersonal, showOnlyActive]);
  return (
    <Container sx={{ mt: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ color: theme.palette.primary.dark, textAlign: 'center' }}>
        {title || "Personal"}
      </Typography>
  
      {/* Siempre muestra los botones, aunque vengan sin texto del backend */}
      <ActionButtons
        refreshData={fetchData}
        buttonText={{
          buttonAddPersonal: buttonText.buttonAddPersonal || "Agregar empleado",
   //       buttonImportPersonal: buttonText.buttonImportPersonal || "Importar empleados"
        }}
      />
  
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          marginBottom: 2,
        }}
      >
        <SearchBar
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          showOnlyActive={showOnlyActive}
          setShowOnlyActive={setShowOnlyActive}
          onRefresh={fetchData}
          filterTexts={filterTexts}
        />
      </Box>
  
      {/* No hay estación seleccionada */}
      {(!selectedStation || !selectedStation.id) && !loading && (
        <Box sx={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          minHeight: '200px', color: theme.palette.text.secondary, mt: 4,
        }}>
          <Typography variant="h6" gutterBottom>
            Seleccioná una estación para mostrar el personal.
          </Typography>
        </Box>
      )}
  
      {/* Hay estación, pero no hay empleados */}
      {selectedStation && selectedStation.id && !loading && filteredData.length === 0 && (
        <Box sx={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          minHeight: '200px', color: theme.palette.text.secondary, mt: 4,
        }}>
          <Typography variant="h6" gutterBottom>
            No hay empleados cargados para esta estación.
          </Typography>
          <Typography variant="body1">
            Usá los botones de arriba para agregar el primero o importar desde Excel.
          </Typography>
        </Box>
      )}
  
      {/* Mostrar la tabla solo si hay datos o si está cargando */}
      {(loading || filteredData.length > 0) && (
        <TableList listPersonal={{ ...listPersonal, data: filteredData }} loading={loading} />
      )}
    </Container>
  );
  
}

export default PagePersonalList;
